import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';

import {
  BANK_LOCATION_KINDS,
  queryNeedsProjectContext,
} from 'scenes/ProjectBank/utils/bankLocation';
import { getFolderAncestorIds } from 'scenes/ProjectBank/utils/folderAncestors';
import { findProjectRootFolder } from 'scenes/ProjectBank/utils/projectRootFolder';
import {
  applyBankLocation,
  revealProjectBankTreeFolder,
} from '../actions/project-bank.actions';
import {
  resolveBankLocation,
  UNKNOWN_BANK_LOCATION,
} from '../selectors/project-bank-location.selectors';
import {
  selectPrivateProjectBankItemsEntities,
  selectProjectFolderFilePermissions,
  selectProjectFolderFilePermissionsArray,
  selectSelectedProjectBankFolderId,
} from '../selectors/project-bank.selectors';
import { loadProjectBankFormsFolderContents } from './loadProjectBankFormsFolderContents.thunk';
import { selectProjectBankFolder } from './selectProjectBankFolder.thunk';

const notify = (key) => {
  enqueueSnackbar(i18n.t(key), { variant: 'info' });
};

/**
 * Opens what the URL asks for (utils/bankLocation), once the folders are
 * loaded: a public folder (the tree opened down to it), the tilaajavastuu
 * view of a project, or a private folder — each as one state change
 * (applyBankLocation), then the folder's contents are loaded. A forms folder
 * of a project whose forms are not loaded yet is loaded first. What cannot
 * be honoured falls back — a private folder that is gone to the private
 * root, anything else to the project's root folder — with one notice, so a
 * stale or foreign link lands somewhere rather than on an empty bank.
 *
 * The private bank, the tilaajavastuu view and the forms folders need the
 * project's context: its entity and the user's permissions. `context` is
 * `{canOpenPrivate, canOpenTilaajavastuu}` once those are known; with null
 * such a location is left alone (`deferred: true`) — a guard, since the
 * scene's URL sync waits for the context before asking. A landing in the
 * private bank also gives the public bank underneath its root folder, so
 * the public tab does not open on nothing. A call that waited for a load
 * and was overtaken by a newer one stops (`superseded: true`).
 *
 * @param {{query: Object, projectId: string, context: ({canOpenPrivate: boolean, canOpenTilaajavastuu: boolean}|null)}} arg
 *   query: parseBankLocationQuery's result; projectId: the route project
 * @returns {{applied: Object|null, fallback?: boolean, deferred?: boolean, superseded?: boolean, reason?: string}}
 */
export const applyProjectBankUrlLocation = createAsyncThunk(
  'projectBank/applyProjectBankUrlLocation',
  async (
    { query, projectId, context = null },
    { dispatch, getState, requestId }
  ) => {
    if (queryNeedsProjectContext(query) && !context) {
      return { applied: null, deferred: true };
    }

    let resolved = resolveBankLocation(getState(), query, projectId);

    if (
      resolved.kind === UNKNOWN_BANK_LOCATION &&
      resolved.reason === 'forms-not-loaded'
    ) {
      await dispatch(
        loadProjectBankFormsFolderContents(resolved.formsProjectId)
      );
      if (getState().projectBank.latestUrlApplyRequestId !== requestId) {
        return { applied: null, superseded: true };
      }
      resolved = resolveBankLocation(getState(), query, projectId);
    }

    const foldersById = selectProjectFolderFilePermissions(getState());

    const openPublicFolder = (folder) => {
      dispatch(
        applyBankLocation({
          location: {
            kind: BANK_LOCATION_KINDS.PUBLIC,
            folderId: folder.id,
            path: folder.path,
          },
          treeFolderIdsToOpen: [
            ...getFolderAncestorIds(foldersById, folder.id),
            folder.id,
          ],
        })
      );
      dispatch(
        selectProjectBankFolder({
          folderId: folder.id,
          path: folder.path,
          skipSetSelected: true,
        })
      );
      // Scrolls the row into view; a virtual forms folder has no row of its
      // own to reveal and the reducer lets it be.
      dispatch(revealProjectBankTreeFolder(folder.id));
    };

    // The public bank's folder while the private bank is landed in: the root,
    // unless a public folder is selected already.
    const publicFolderUnderneath = () => {
      if (selectSelectedProjectBankFolderId(getState())) return null;
      const root = findProjectRootFolder(
        selectProjectFolderFilePermissionsArray(getState()),
        projectId
      );
      return root ? { folderId: root.id, path: root.path } : null;
    };
    const loadFolderContents = (publicFolder) => {
      if (!publicFolder) return;
      dispatch(
        selectProjectBankFolder({
          folderId: publicFolder.folderId,
          path: publicFolder.path,
          skipSetSelected: true,
        })
      );
    };

    const openRoot = (messageKey, reason) => {
      const root = findProjectRootFolder(
        selectProjectFolderFilePermissionsArray(getState()),
        projectId
      );
      if (root) {
        openPublicFolder(root);
        if (messageKey) notify(messageKey);
      }
      return {
        applied: root
          ? { kind: BANK_LOCATION_KINDS.PUBLIC, folderId: root.id }
          : null,
        fallback: true,
        reason,
      };
    };

    switch (resolved.kind) {
      case BANK_LOCATION_KINDS.PUBLIC: {
        openPublicFolder(foldersById[resolved.folderId]);
        return { applied: resolved };
      }

      case BANK_LOCATION_KINDS.TILAAJAVASTUU: {
        if (!context.canOpenTilaajavastuu) {
          return openRoot('bank.link_no_permission', 'no-permission');
        }
        // The view's tree row sits under the project's folder row.
        const projectFolder = findProjectRootFolder(
          Object.values(foldersById),
          resolved.projectId
        );
        dispatch(
          applyBankLocation({
            location: resolved,
            treeFolderIdsToOpen: projectFolder
              ? [
                  ...getFolderAncestorIds(foldersById, projectFolder.id),
                  projectFolder.id,
                ]
              : [],
          })
        );
        return { applied: resolved };
      }

      case BANK_LOCATION_KINDS.PRIVATE: {
        if (!context.canOpenPrivate) {
          return openRoot('bank.link_no_permission', 'no-permission');
        }
        const item = resolved.folderId
          ? selectPrivateProjectBankItemsEntities(getState())?.[
              resolved.folderId
            ]
          : null;
        // A private item's path is the chain of its ancestors' ids, its own
        // last.
        const privateTreeFolderIdsToOpen = String(item?.path || '')
          .split('/')
          .filter((id) => id && id !== resolved.folderId);
        const publicFolder = publicFolderUnderneath();
        dispatch(
          applyBankLocation({
            location: resolved,
            privateTreeFolderIdsToOpen,
            publicFolder,
          })
        );
        loadFolderContents(publicFolder);
        return { applied: resolved };
      }

      default: {
        if (resolved.fallback?.kind === BANK_LOCATION_KINDS.PRIVATE) {
          if (!context.canOpenPrivate) {
            return openRoot('bank.link_no_permission', 'no-permission');
          }
          const publicFolder = publicFolderUnderneath();
          dispatch(
            applyBankLocation({ location: resolved.fallback, publicFolder })
          );
          loadFolderContents(publicFolder);
          notify('bank.link_folder_not_found');
          return {
            applied: resolved.fallback,
            fallback: true,
            reason: resolved.reason,
          };
        }
        if (resolved.reason === 'no-root') {
          // Nothing loaded to fall back to: the bank is empty either way.
          return { applied: null, fallback: true, reason: resolved.reason };
        }
        return openRoot(
          query.isEmpty ? null : 'bank.link_folder_not_found',
          resolved.reason
        );
      }
    }
  }
);
